/**
 * OrgSwitcher — global organization switcher for Triveny platform operators,
 * plus the always-on impersonation banner.
 *
 *  - Operator (Triveny.ai admin): a fixed pill (bottom-left) opens a searchable
 *    list of every customer sign-up; picking one opens that workspace as its
 *    admin (full impersonation).
 *  - While impersonating: a prominent top banner shows which workspace is open
 *    and offers "Return to Triveny.ai".
 *
 * Operator detection is implicit: GET /auth/organizations is operator-gated
 * (403 for everyone else), so a non-operator simply gets no list and the
 * switcher never renders. Triveny-only.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Search, X, LogOut, Loader2, ChevronRight, Eye } from 'lucide-react';
import { config } from '@/src/config';
import { tvTokens } from '@/src/platform/shared/components/triveny';
import {
  getImpersonation,
  startImpersonation,
  exitImpersonation,
} from '@/src/platform/shared/utils/impersonation';

interface OrgRow {
  id: string;
  slug: string;
  name: string;
  source: string | null;
  status: string;
  user_count: number;
  admin_email: string | null;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export default function OrgSwitcher() {
  const base = config.weaveinApi?.baseUrl || '';
  const impersonation = getImpersonation();

  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<OrgRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // Whether the current user is an operator (org directory returned 200).
  const [isOperator, setIsOperator] = useState(false);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${base}/api/platform/auth/organizations`, { headers: authHeaders() });
      if (res.status === 403) {
        setIsOperator(false);
        setOrgs([]);
        return;
      }
      if (!res.ok) throw new Error(`(${res.status})`);
      const body = await res.json();
      setIsOperator(true);
      setOrgs(Array.isArray(body?.organizations) ? body.organizations : []);
    } catch (e: any) {
      setError(`Couldn't load organizations ${e?.message || ''}`.trim());
    } finally {
      setLoading(false);
    }
  }, [base]);

  // Detect operator status once on mount (Triveny only, and not while
  // impersonating — the impersonation token isn't an operator token).
  useEffect(() => {
    if (!config.isTriveny || impersonation) return;
    void loadOrgs();
  }, [loadOrgs, impersonation]);

  const jumpTo = async (org: OrgRow) => {
    setBusyId(org.id);
    setError(null);
    try {
      await startImpersonation(base, org); // reloads on success
    } catch (e: any) {
      setError(e?.message || 'Could not open that workspace');
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = orgs ?? [];
    if (!q) return list;
    return list.filter(
      (o) =>
        o.name?.toLowerCase().includes(q) ||
        o.slug?.toLowerCase().includes(q) ||
        (o.admin_email || '').toLowerCase().includes(q),
    );
  }, [orgs, query]);

  // ── Impersonation banner (highest priority) ──────────────────────────────
  if (impersonation) {
    return (
      <div
        role="status"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2147483000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: '8px 16px',
          background: '#7c2d12',
          color: '#fff',
          fontFamily: tvTokens.fontSans,
          fontSize: 13,
          boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
        }}
      >
        <Eye size={15} />
        <span>
          Viewing <strong>{impersonation.name}</strong> as operator
          {impersonation.impersonator ? ` · ${impersonation.impersonator}` : ''}
        </span>
        <button
          onClick={exitImpersonation}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 12px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.6)',
            background: 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <LogOut size={13} /> Return to Triveny.ai
        </button>
      </div>
    );
  }

  // ── Operator switcher (only renders once we know they're an operator) ─────
  if (!config.isTriveny || !isOperator) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Switch organization"
        style={{
          position: 'fixed',
          left: 16,
          bottom: 16,
          zIndex: 2147482000,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderRadius: 999,
          border: `1px solid ${tvTokens.border}`,
          background: tvTokens.surface,
          color: tvTokens.ink,
          fontFamily: tvTokens.fontDisplay,
          fontSize: 12.5,
          fontWeight: 600,
          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
          cursor: 'pointer',
        }}
      >
        <Building2 size={14} color={tvTokens.accent} /> Switch org
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147482500,
            background: 'rgba(15,15,20,0.35)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '10vh 16px 16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 520,
              maxHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
              background: tvTokens.paper,
              border: `1px solid ${tvTokens.border}`,
              borderRadius: tvTokens.r,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden',
              fontFamily: tvTokens.fontSans,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: `1px solid ${tvTokens.borderMuted}` }}>
              <Building2 size={16} color={tvTokens.accent} />
              <span style={{ fontFamily: tvTokens.fontDisplay, fontWeight: 600, fontSize: 15, color: tvTokens.ink, flex: 1 }}>
                Open a workspace
              </span>
              <button onClick={() => setOpen(false)} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: tvTokens.ink500 }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ position: 'relative', padding: '10px 14px', borderBottom: `1px solid ${tvTokens.borderMuted}` }}>
              <Search size={15} color={tvTokens.ink400} style={{ position: 'absolute', left: 26, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search organizations…"
                style={{
                  width: '100%',
                  height: 36,
                  padding: '0 12px 0 34px',
                  borderRadius: tvTokens.rSm,
                  border: `1px solid ${tvTokens.border}`,
                  background: tvTokens.surface,
                  color: tvTokens.ink,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loading ? (
                <div style={{ padding: 22, display: 'flex', alignItems: 'center', gap: 8, color: tvTokens.ink400 }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
                </div>
              ) : error ? (
                <div style={{ padding: 18, color: tvTokens.danger, fontSize: 13.5 }}>{error}</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: 18, color: tvTokens.ink400, fontSize: 13.5 }}>No organizations found.</div>
              ) : (
                filtered.map((o, i) => (
                  <button
                    key={o.id}
                    onClick={() => jumpTo(o)}
                    disabled={busyId != null}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 16px',
                      borderWidth: 0,
                      borderTop: i === 0 ? 'none' : `1px solid ${tvTokens.borderMuted}`,
                      background: 'transparent',
                      cursor: busyId != null ? 'wait' : 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: tvTokens.ink }}>{o.name}</div>
                      <div style={{ fontSize: 12, color: tvTokens.ink400 }}>
                        {o.admin_email || o.slug} · {o.user_count} user{o.user_count === 1 ? '' : 's'}
                      </div>
                    </div>
                    {busyId === o.id ? (
                      <Loader2 size={15} style={{ animation: 'spin 1s linear infinite', color: tvTokens.accent }} />
                    ) : (
                      <ChevronRight size={16} color={tvTokens.ink400} />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
